"use client";

import * as React from "react";
import { type DialogProps } from "@radix-ui/react-dialog";
import { Command as CommandPrimitive, useCommandState } from "cmdk";
import { Search } from "lucide-react";

import { cn } from "../utils";
import { Dialog, DialogContent, DialogTitle } from "./dialog";

export interface CommandProps extends React.ComponentProps<typeof CommandPrimitive> {}

function Command({ className, ...props }: CommandProps) {
  return (
    <CommandPrimitive
      data-slot="command"
      className={cn(
        "flex h-full w-full flex-col overflow-hidden rounded-md bg-popover text-popover-foreground",
        className,
      )}
      {...props}
    />
  );
}

export interface CommandDialogProps extends DialogProps {
  shouldFilter?: boolean;
  loop?: boolean;
}

function CommandDialog({
  children,
  shouldFilter = true,
  loop = false,
  ...props
}: CommandDialogProps) {
  return (
    <Dialog {...props}>
      <DialogContent className="overflow-hidden p-0" showCloseButton={false}>
        <DialogTitle className="sr-only">Command Palette</DialogTitle>
        <Command
          shouldFilter={shouldFilter}
          loop={loop}
          className="[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-muted-foreground [&_[cmdk-group]:not([hidden])_~[cmdk-group]]:pt-0 [&_[cmdk-group]]:px-2 [&_[cmdk-input-wrapper]_svg]:h-5 [&_[cmdk-input-wrapper]_svg]:w-5 [&_[cmdk-input]]:h-12 [&_[cmdk-item]]:px-2 [&_[cmdk-item]]:py-3 [&_[cmdk-item]_svg]:h-5 [&_[cmdk-item]_svg]:w-5"
        >
          {children}
        </Command>
      </DialogContent>
    </Dialog>
  );
}

export interface CommandInputProps extends React.ComponentProps<typeof CommandPrimitive.Input> {}

function CommandInput({ className, ...props }: CommandInputProps) {
  return (
    <div className="flex items-center border-b px-3" cmdk-input-wrapper="">
      <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
      <CommandPrimitive.Input
        data-1p-ignore
        data-slot="command-input"
        className={cn(
          "flex h-10 w-full rounded-md bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50",
          className,
        )}
        {...props}
      />
    </div>
  );
}

export interface CommandListProps extends React.ComponentProps<typeof CommandPrimitive.List> {}

function CommandList({ className, ...props }: CommandListProps) {
  const listRef = React.useRef<HTMLDivElement>(null);
  const search = useCommandState((state) => state.search);

  // cmdk reorders filtered items in the DOM but keeps scrollTop, so the best
  // matches can end up above the viewport. Reset after cmdk's scrollIntoView.
  // https://github.com/dip/cmdk/issues/389
  React.useEffect(() => {
    const list = listRef.current;
    if (!list) return;
    const frame = requestAnimationFrame(() => {
      list.scrollTop = 0;
    });
    return () => cancelAnimationFrame(frame);
  }, [search]);

  return (
    <CommandPrimitive.List
      ref={listRef}
      data-slot="command-list"
      // Stop wheel events from reaching react-remove-scroll's document-level
      // listener. When a Command popover is portalled out of a modal Dialog, that
      // listener treats the list as "outside" the locked content and
      // preventDefault()s the wheel, so the list scrolls by keyboard but not by
      // mouse. Halting propagation here lets the list scroll natively (and stops
      // scroll-chaining to the page behind the dropdown either way).
      onWheel={(event) => event.stopPropagation()}
      className={cn(
        "max-h-[300px] overflow-y-auto overflow-x-hidden [overflow-anchor:none]",
        className,
      )}
      {...props}
    />
  );
}

export interface CommandEmptyProps extends React.ComponentProps<typeof CommandPrimitive.Empty> {}

function CommandEmpty({ ...props }: CommandEmptyProps) {
  return (
    <CommandPrimitive.Empty
      data-slot="command-empty"
      className="py-6 text-center text-sm"
      {...props}
    />
  );
}

export interface CommandGroupProps extends React.ComponentProps<typeof CommandPrimitive.Group> {}

function CommandGroup({ className, ...props }: CommandGroupProps) {
  return (
    <CommandPrimitive.Group
      data-slot="command-group"
      className={cn(
        "overflow-hidden p-1 text-foreground [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-muted-foreground",
        className,
      )}
      {...props}
    />
  );
}

export interface CommandSeparatorProps extends React.ComponentProps<
  typeof CommandPrimitive.Separator
> {}

function CommandSeparator({ className, ...props }: CommandSeparatorProps) {
  return (
    <CommandPrimitive.Separator
      data-slot="command-separator"
      className={cn("-mx-1 h-px bg-border", className)}
      {...props}
    />
  );
}

export interface CommandItemProps extends React.ComponentProps<typeof CommandPrimitive.Item> {}

function CommandItem({ className, ...props }: CommandItemProps) {
  return (
    <CommandPrimitive.Item
      data-slot="command-item"
      className={cn(
        "relative flex cursor-default gap-2 select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none data-[disabled=true]:pointer-events-none data-[selected=true]:bg-accent data-[selected=true]:text-accent-foreground data-[disabled=true]:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
        className,
      )}
      {...props}
    />
  );
}

export interface CommandShortcutProps extends React.ComponentProps<"span"> {}

function CommandShortcut({ className, ...props }: CommandShortcutProps) {
  return (
    <span
      data-slot="command-shortcut"
      className={cn("ml-auto text-xs tracking-widest text-muted-foreground", className)}
      {...props}
    />
  );
}

export {
  Command,
  CommandDialog,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandShortcut,
  CommandSeparator,
};
